import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Trophy, Loader2, GitCompareArrows } from 'lucide-react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { UserAvatar } from '@/components/UserAvatar';
import { GlobalLeaderboardEntry } from '@/types/bracket';
import { fetchGlobalLeaderboard, fetchAllBracketEntries } from '@/lib/bracket-queries';
import { buildGlobalLeaderboard } from '@/lib/bracket-scoring';
import { fetchSeries } from '@/lib/supabase-queries';

// ── Rank styling ──────────────────────────────────────────────────────────────

const rankColors: Record<number, { text: string; border: string; glow: string }> = {
  1: { text: 'text-amber-400', border: 'border-accent/40', glow: 'shadow-[0_0_24px_-4px_hsl(24_95%_53%/0.15)]' },
  2: { text: 'text-slate-300', border: 'border-slate-400/30', glow: '' },
  3: { text: 'text-amber-600', border: 'border-amber-700/30', glow: '' },
};

// ── Component ─────────────────────────────────────────────────────────────────

const GlobalLeaderboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [entries, setEntries] = useState<GlobalLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Strategy 1: try server-side calculated scores
        let serverEntries = await fetchGlobalLeaderboard();

        if (serverEntries.length > 0) {
          setEntries(serverEntries);
          return;
        }

        // Strategy 2: client-side fallback — fetch entries + series, build leaderboard
        const [bracketEntries, seriesList] = await Promise.all([
          fetchAllBracketEntries(),
          fetchSeries(),
        ]);

        const leaderboard = buildGlobalLeaderboard(bracketEntries, seriesList);
        setEntries(leaderboard);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'שגיאה בטעינת הטבלה');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  // Only show compare buttons if current user has a bracket entry
  const currentUserHasBracket = currentUserId
    ? entries.some(e => e.user_id === currentUserId)
    : false;

  const handleCompare = (e: React.MouseEvent, targetUserId: string) => {
    e.stopPropagation();
    if (!currentUserId) return;
    navigate(`/bracket/compare/${currentUserId}/${targetUserId}`);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-2xl overflow-x-hidden" dir="rtl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-8"
        >
          <Trophy className="w-10 h-10 text-accent mx-auto mb-3" />
          <h1 className="text-3xl font-bold tracking-tight">טבלת אלופים גלובלית</h1>
          <p className="text-muted-foreground mt-1 text-sm">דירוג כל המשתתפים בברקט</p>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-card border border-destructive/30 rounded-2xl p-6 text-center">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && entries.length === 0 && (
          <div className="text-center py-20">
            <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
            <p className="text-muted-foreground font-medium">עדיין אין משתתפים בברקט</p>
            <p className="text-xs text-muted-foreground mt-1">הדירוג יתעדכן כשסדרות יסתיימו</p>
          </div>
        )}

        {/* Leaderboard */}
        {!loading && !error && entries.length > 0 && (
          <div className="space-y-4">
            {/* Subtitle hint */}
            <p className="text-xs text-muted-foreground text-center mb-4">
              הדירוג יתעדכן כשסדרות יסתיימו
            </p>

            {/* Top 3 — large cards */}
            {top3.map((entry, i) => {
              const rank = entry.rank;
              const colors = rankColors[rank] || rankColors[3];

              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                  className={`bg-card border ${colors.border} rounded-2xl p-6 ${colors.glow} cursor-pointer hover:-translate-y-0.5 transition-transform`}
                  onClick={() => navigate(`/bracket/${entry.user_id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <span className={`stat-number text-5xl tabular-nums ${colors.text}`}>
                          {rank}
                        </span>
                        {rank === 1 && (
                          <Crown className="w-5 h-5 text-amber-400 absolute -top-3 left-1/2 -translate-x-1/2" />
                        )}
                      </div>
                      <UserAvatar avatarId={entry.avatar_id ?? null} displayName={entry.display_name} size="md" />
                      <div>
                        <p className="text-lg font-bold">{entry.display_name}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-muted-foreground">
                            {entry.correct_winners} מנצחים
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {entry.correct_scores} תוצאות מדויקות
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {currentUserHasBracket && entry.user_id !== currentUserId && (
                        <button
                          onClick={(e) => handleCompare(e, entry.user_id)}
                          className="p-2 rounded-xl hover:bg-accent/10 transition-colors text-muted-foreground hover:text-accent"
                          title="השווה ברקט"
                        >
                          <GitCompareArrows className="w-5 h-5" />
                        </button>
                      )}
                      <div className="text-left">
                        <p className="text-3xl font-bold text-accent tracking-tight tabular-nums">
                          {entry.total_points}
                        </p>
                        <p className="section-label">נקודות</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Rest — compact rows */}
            {rest.length > 0 && (
              <div className="space-y-2 mt-2">
                {/* Table header */}
                <div className="flex items-center justify-between px-4 py-1">
                  <div className="flex items-center gap-3">
                    <span className="section-label w-8 text-center">דירוג</span>
                    <span className="section-label">שם</span>
                  </div>
                  <div className="flex items-center gap-6 text-left">
                    <span className="section-label hidden sm:block">מנצחים</span>
                    <span className="section-label hidden sm:block">תוצאות</span>
                    <span className="section-label">נקודות</span>
                  </div>
                </div>

                {rest.map((entry, i) => (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (i + 3) * 0.05, duration: 0.3 }}
                    className="bg-card rounded-xl p-4 flex items-center justify-between cursor-pointer hover:-translate-y-0.5 transition-transform"
                    onClick={() => navigate(`/bracket/${entry.user_id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold w-8 text-center text-muted-foreground tabular-nums">
                        {entry.rank}
                      </span>
                      <UserAvatar avatarId={entry.avatar_id ?? null} displayName={entry.display_name} size="sm" />
                      <div>
                        <p className="font-semibold text-sm">{entry.display_name}</p>
                        <p className="text-xs text-muted-foreground sm:hidden">
                          {entry.correct_winners} מנצחים · {entry.correct_scores} מדויקות
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-left">
                      <span className="text-sm text-muted-foreground hidden sm:block tabular-nums">
                        {entry.correct_winners}
                      </span>
                      <span className="text-sm text-muted-foreground hidden sm:block tabular-nums">
                        {entry.correct_scores}
                      </span>
                      <span className="text-accent font-bold tabular-nums">
                        {entry.total_points}
                      </span>
                      {currentUserHasBracket && entry.user_id !== currentUserId && (
                        <button
                          onClick={(e) => handleCompare(e, entry.user_id)}
                          className="p-1.5 rounded-lg hover:bg-accent/10 transition-colors text-muted-foreground hover:text-accent"
                          title="השווה ברקט"
                        >
                          <GitCompareArrows className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GlobalLeaderboardPage;
